import { Elena, html, unsafeHTML, nothing } from "@theme/elena";
import { Component } from "@theme/component";

export class FslButtonClickEvent extends CustomEvent {
  static eventName = "fsl-button:click";

  constructor(detail = {}) {
    super(FslButtonClickEvent.eventName, {
      detail,
      bubbles: true,
      composed: true,
      cancelable: true,
    });
  }
}

/**
 * Button is used for interface actions.
 *
 * @displayName Button
 * @status alpha
 *
 * @attr {string} [variant] - solid | surface | subtle | outline | plain | branded | unbranded | poster | light | control | icon-only. Default: solid.
 * @attr {string} [size] - 2xs | xs | sm | md | lg | xl | 2xl. Default: md.
 * @attr {boolean} [disabled] - Disables interaction.
 * @attr {boolean} [loading] - Marks the button busy.
 * @attr {boolean} [expand] - Stretches the button to fill its container.
 * @attr {string} [href] - When set, renders an <a> instead of a <button>.
 * @attr {string} [target] - Anchor target. Only applies when href is set.
 * @attr {boolean} [download] - Adds the download attribute on <a>.
 * @attr {string} [label] - aria-label fallback when there is no visible text.
 * @attr {string} [type] - button | submit | reset. Default: button.
 * @attr {string} [name] - Form-association name.
 * @attr {string} [value] - Form-association value.
 *
 * @event fsl-button:click - Fired when the button is activated.
 */
export default class Button extends Elena(Component) {
  static tagName = "fsl-button";
  static formAssociated = true;

  static observedAttributes = [
    "variant",
    "size",
    "disabled",
    "loading",
    "expand",
    "label",
    "href",
    "target",
    "download",
    "name",
    "value",
    "type",
  ];

  #internals = this.attachInternals?.();
  #originalHTML = "";

  connectedCallback() {
    if (!this.#originalHTML) {
      this.#originalHTML = this.innerHTML;
    }
    super.connectedCallback?.();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (this._hydrated) this.requestUpdate?.();
  }

  updated() {
    super.updated?.();
    this.updatedCallback?.();
    if (!this.hasAttribute("hydrated")) {
      this.setAttribute("hydrated", "");
    }
  }

  handleClick(event) {
    if (this.hasAttribute("disabled") || this.hasAttribute("loading")) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    this.dispatchEvent(new FslButtonClickEvent({ originalEvent: event }));
  }

  /** @internal */
  render() {
    const isDisabled = this.hasAttribute("disabled");
    const isLoading = this.hasAttribute("loading");
    const href = this.getAttribute("href");
    const label = this.getAttribute("label");
    const type = this.getAttribute("type") ?? "button";
    const name = this.getAttribute("name");
    const value = this.getAttribute("value");
    const target = this.getAttribute("target") ?? "_self";
    const download = this.hasAttribute("download");
    const content = this.#originalHTML || this.text || "";

    if (href) {
      const safeHref = /^(https?:|mailto:|tel:|\/|#)/i.test(href) ? href : "#";
      return html`
        <a
          ref="root"
          class="button"
          href="${safeHref}"
          target="${target}"
          ${download ? "download" : nothing}
          ${label ? html`aria-label="${label}"` : nothing}
          on:click="/handleClick"
        >${unsafeHTML(content)}</a>
      `;
    }

    return html`
      <button
        ref="root"
        class="button"
        type="${type}"
        ${name ? html`name="${name}"` : nothing}
        ${value ? html`value="${value}"` : nothing}
        ${isDisabled ? "disabled" : nothing}
        ${isLoading ? html`aria-disabled="true"` : nothing}
        ${label ? html`aria-label="${label}"` : nothing}
        on:click="/handleClick"
      >${unsafeHTML(content)}</button>
    `;
  }
}

Button.define();
